import { api } from 'constants/constant';
import { getHeaders } from 'auth/auth';
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';

/**
 * DSCR — investor loans qualified on the property rather than the borrower.
 *
 * One tag, `Dscr`, and every mutation invalidates it.
 *
 * That is deliberate rather than lazy. The five screens are not five
 * independent forms: the programme sets the minimum ratio, the rent roll sets
 * the numerator, the payment sets the denominator, and the eligibility verdict
 * is a function of all of them. Editing one unit's rent changes the verdict in
 * the header. Tagging them separately would let the strip show Eligible while
 * the rent that made it so had already been edited away.
 *
 * `GET /` returns the whole file in one call — profile, entities, units, STR,
 * the latest calculation and the findings — so the shell fetches once and every
 * panel reads from the same snapshot.
 */
const BASE = (loanId) => `/b2b/loan/applications/${loanId}/dscr`;

/** Every endpoint answers `{status, message, data}`. */
const unwrap = (response) => response?.data ?? null;
const unwrapList = (response) => response?.data ?? [];

export const dscrAPI = createApi({
  reducerPath: 'dscr',
  tagTypes: ['Dscr'],
  baseQuery: fetchBaseQuery({ baseUrl: api, credentials: 'include' }),
  endpoints: (builder) => ({
    /** The whole file. `null` when this loan has no DSCR profile yet. */
    getDscrFile: builder.query({
      query: ({ loanId }) => ({
        url: BASE(loanId),
        method: 'GET',
        headers: getHeaders(),
      }),
      transformResponse: unwrap,
      providesTags: ['Dscr'],
    }),

    /**
     * The programmes this tenant may pick, with the minimum each one sets.
     *
     * Served rather than hardcoded in the client so the minimum on the screen
     * and the minimum the calculation applies are the same number. A tenant can
     * override any of them, and a hardcoded copy here would not know.
     */
    getDscrPrograms: builder.query({
      query: ({ loanId }) => ({
        url: `${BASE(loanId)}/programs`,
        method: 'GET',
        headers: getHeaders(),
      }),
      transformResponse: unwrapList,
      providesTags: ['Dscr'],
    }),

    getDscrEligibility: builder.query({
      query: ({ loanId }) => ({
        url: `${BASE(loanId)}/eligibility`,
        method: 'GET',
        headers: getHeaders(),
      }),
      transformResponse: unwrap,
      providesTags: ['Dscr'],
    }),

    getDscrCalculations: builder.query({
      query: ({ loanId, limit = 25 }) => ({
        url: `${BASE(loanId)}/calculations?limit=${limit}`,
        method: 'GET',
        headers: getHeaders(),
      }),
      transformResponse: unwrapList,
      providesTags: ['Dscr'],
    }),

    /** One frozen run, including the per-unit breakdown behind its ratio. */
    getDscrCalculation: builder.query({
      query: ({ loanId, calculationId }) => ({
        url: `${BASE(loanId)}/calculations/${calculationId}`,
        method: 'GET',
        headers: getHeaders(),
      }),
      transformResponse: unwrap,
      providesTags: ['Dscr'],
    }),

    getDscrFindings: builder.query({
      query: ({ loanId }) => ({
        url: `${BASE(loanId)}/findings`,
        method: 'GET',
        headers: getHeaders(),
      }),
      transformResponse: unwrapList,
      providesTags: ['Dscr'],
    }),

    /** Turn this loan into a DSCR file. Idempotent — safe to call on open. */
    enableDscr: builder.mutation({
      query: ({ loanId }) => ({
        url: BASE(loanId),
        method: 'PUT',
        headers: getHeaders(),
      }),
      transformResponse: unwrap,
      invalidatesTags: ['Dscr'],
    }),

    updateDscrProgram: builder.mutation({
      query: ({ loanId, ...body }) => ({
        url: `${BASE(loanId)}/program`,
        method: 'PATCH',
        headers: getHeaders(),
        body,
      }),
      transformResponse: unwrap,
      invalidatesTags: ['Dscr'],
    }),

    updateDscrRental: builder.mutation({
      query: ({ loanId, ...body }) => ({
        url: `${BASE(loanId)}/rental`,
        method: 'PATCH',
        headers: getHeaders(),
        body,
      }),
      transformResponse: unwrap,
      invalidatesTags: ['Dscr'],
    }),

    updateDscrExpenses: builder.mutation({
      query: ({ loanId, ...body }) => ({
        url: `${BASE(loanId)}/expenses`,
        method: 'PATCH',
        headers: getHeaders(),
        body,
      }),
      transformResponse: unwrap,
      invalidatesTags: ['Dscr'],
    }),

    updateDscrReserves: builder.mutation({
      query: ({ loanId, ...body }) => ({
        url: `${BASE(loanId)}/reserves`,
        method: 'PATCH',
        headers: getHeaders(),
        body,
      }),
      transformResponse: unwrap,
      invalidatesTags: ['Dscr'],
    }),

    /**
     * The whole rent roll, replaced.
     *
     * A PUT rather than per-unit PATCHes because the screen edits a table: the
     * rows have no stable id until they are saved, and one write is one brief
     * lock instead of a lock per row held across a long request.
     */
    replaceDscrUnits: builder.mutation({
      query: ({ loanId, units }) => ({
        url: `${BASE(loanId)}/rental/units`,
        method: 'PUT',
        headers: getHeaders(),
        body: { units },
      }),
      transformResponse: unwrapList,
      invalidatesTags: ['Dscr'],
    }),

    /** Entities with their owners nested, replaced together so the two cannot
        get out of step — an owner surviving its entity would count toward an
        ownership total that no longer exists. */
    replaceDscrEntities: builder.mutation({
      query: ({ loanId, entities }) => ({
        url: `${BASE(loanId)}/entities`,
        method: 'PUT',
        headers: getHeaders(),
        body: { entities },
      }),
      transformResponse: unwrapList,
      invalidatesTags: ['Dscr'],
    }),

    updateDscrShortTermRental: builder.mutation({
      query: ({ loanId, ...body }) => ({
        url: `${BASE(loanId)}/short-term-rental`,
        method: 'PATCH',
        headers: getHeaders(),
        body,
      }),
      transformResponse: unwrap,
      invalidatesTags: ['Dscr'],
    }),

    /** Run the calculation. Append-only — never rewrites an earlier run. */
    runDscrCalculation: builder.mutation({
      query: ({ loanId }) => ({
        url: `${BASE(loanId)}/calculations`,
        method: 'POST',
        headers: getHeaders(),
      }),
      transformResponse: unwrap,
      invalidatesTags: ['Dscr'],
    }),

    /**
     * Accept a defect, on the record.
     *
     * The justification is mandatory and is the only part of this anybody reads
     * later. A fatal finding cannot be signed; the server refuses with a 400.
     */
    signDscrException: builder.mutation({
      query: ({ loanId, findingId, justification }) => ({
        url: `${BASE(loanId)}/findings/${findingId}/exception`,
        method: 'POST',
        headers: getHeaders(),
        body: { justification },
      }),
      transformResponse: unwrap,
      invalidatesTags: ['Dscr'],
    }),
  }),
});

export const {
  useGetDscrFileQuery,
  useGetDscrProgramsQuery,
  useGetDscrEligibilityQuery,
  useGetDscrCalculationsQuery,
  useGetDscrCalculationQuery,
  useGetDscrFindingsQuery,
  useEnableDscrMutation,
  useUpdateDscrProgramMutation,
  useUpdateDscrRentalMutation,
  useUpdateDscrExpensesMutation,
  useUpdateDscrReservesMutation,
  useReplaceDscrUnitsMutation,
  useReplaceDscrEntitiesMutation,
  useUpdateDscrShortTermRentalMutation,
  useRunDscrCalculationMutation,
  useSignDscrExceptionMutation,
} = dscrAPI;
